import moment from 'moment'
import { toHumanReadable, decodeEntities } from 'beam/dist/utils'
import evaluationState from './const/evaluationState'
import evaluationResponseState from './const/evaluationResponseState'
import sentiment from './const/sentiment'
import { COLUMN_CATEGORY } from './recordings.constants'
import { formatMediaDisplayState } from './helpers'

/**
 * Map of column IDs to column definitions from a contact
 */

export const mapColumns = (columns, contact, willUseUserTimezone, userTimezone) => {
  const row = columns.map(column => {
    let value
    if (column.category === COLUMN_CATEGORY.METADATA) {
      value = (() => {
        const metadataField = contact.metadata && contact.metadata[column.key] ? contact.metadata[column.key] : null
        return { translate: false, value: metadataField && metadataField.value } // TODO: either here, or later in the process, make the hyperlink metadata an actual link
      })()
    } else {
      value = (() => {
        switch (column.key) {
          case 'id':
            return { translate: false, value: contact.id }
          case 'agent_acd_id':
            return { translate: false, value: contact.agent.agentAcdId }
          case 'last_name':
            return { translate: false, value: contact.agent.lastName }
          case 'first_name':
            return { translate: false, value: contact.agent.firstName }
          case 'group_name':
            return { translate: false, value: contact.group.name }
          case 'team_name':
            return { translate: false, value: contact.team.name }
          case 'ani':
            return { translate: false, value: contact.ani }
          case 'line':
            return { translate: false, value: contact.line }
          case 'dnis':
            return { translate: false, value: contact.dnis }
          case 'direction':
            return { translate: true, value: contact.isInbound ? 'inbound' : 'outbound' }
          case 'date':
            return {
              translate: false,
              value: formatDate(contact.startTime, willUseUserTimezone ? userTimezone : contact.tz),
            }
          case 'time':
            return {
              translate: false,
              value: moment(contact.startTime)
                .tz(willUseUserTimezone ? userTimezone : contact.tz)
                .format('hh:mm a')
                .toUpperCase(),
            }
          case 'tz':
            return { translate: false, value: willUseUserTimezone ? userTimezone : contact.tz }
          case 'retention_policy':
            return { translate: false, value: contact.retentionPolicyName }
          case 'evaluation_form_name':
            return { translate: false, value: contact.evalForm ? contact.evalForm.name : '' }
          case 'evaluation_score':
            return {
              translate: false,
              value:
                contact.evaluation && (contact.evaluation.totalScore || contact.evaluation.totalScore === 0)
                  ? Number.parseFloat(contact.evaluation.totalScore).toFixed(2)
                  : '',
            }
          case 'evaluation_state':
            if (contact.evaluation) {
              switch (
                contact.evaluation.stateId // sorry not sorry
              ) {
                case evaluationState.SCORED:
                  return { translate: true, value: 'scored' }
                case evaluationState.IN_PROGRESS:
                  return { translate: true, value: 'inProgress' }
                case evaluationState.NEEDS_APPROVAL:
                  return { translate: true, value: 'needsApproval' }
                case evaluationState.CANNOT_SCORE:
                  return { translate: true, value: 'cannotScore' }
                case evaluationState.CANNOT_SCORE_APPROVAL:
                  return { translate: true, value: 'cannotScoreApproval' }
                case evaluationState.UNSCORED:
                /* falls through */
                default:
                  return { translate: true, value: 'unscored' }
              }
            }
            return { translate: false, value: '' }
          case 'evaluation_date':
            return {
              translate: false,
              value: contact.evaluation !== null ? formatDate(contact.evaluation.evaluated, willUseUserTimezone ? userTimezone : contact.tz) : '',
            }
          case 'evaluation_calibration':
            return { translate: true, value: formatYesNo(contact.isCalibration) }
          case 'evaluator_name':
            return {
              translate: false,
              value:
                contact.evaluator && contact.evaluator.firstName !== null && contact.evaluator.lastName !== null
                  ? `${contact.evaluator.firstName} ${contact.evaluator.lastName}`
                  : '',
            }
          case 'eval_response_date':
            return {
              translate: false,
              value:
                contact.evaluation && contact.evaluation.responseDate
                  ? formatDate(contact.evaluation.responseDate, willUseUserTimezone ? userTimezone : contact.tz)
                  : '',
            }
          case 'eval_response_state':
            if (contact.evaluation && contact.evaluation.responseState) {
              switch (contact.evaluation.responseState) {
                case evaluationResponseState.PENDING:
                  return { translate: true, value: 'pendingResponse' }
                case evaluationResponseState.ACKNOWLEDGED:
                  return { translate: true, value: 'acknowledged' }
                case evaluationResponseState.APPEALED:
                  return { translate: true, value: 'appealed' }
                default:
              }
            }
            return { translate: false, value: '' }
          case 'eval_score_prediction':
            return { translate: false, value: contact.evalScorePrediction }
          case 'net_promoter_score_prediction':
            return { translate: false, value: contact.netPromoterScorePrediction }
          case 'net_promoter_score':
            return { translate: false, value: contact.netPromoterScore }
          case 'surveyScore':
            return { translate: false, value: contact.surveyScore }
          case 'surveyName':
            return { translate: false, value: contact.surveyName }
          case 'approved_by':
            return {
              translate: false,
              value:
              contact.approver && contact.approver.firstName !== null && contact.approver.lastName !== null
                ? `${contact.approver.firstName} ${contact.approver.lastName}`
                : '',
            }
          case 'quality_reason':
            return {
              translate: Boolean(contact.reason),
              value: contact.reason ? contact.reason.key : '',
            }
          case 'archive_reason':
            return { translate: true, value: contact.archiveReason ? contact.archiveReason.key : '' }
          case 'call_duration':
            return { translate: false, value: formatDuration(contact.callDuration) }
          case 'hr':
            return { translate: true, value: formatYesNo(contact.hr) }
          case 'training':
            return { translate: true, value: formatYesNo(contact.training) }
          case 'to_address':
            return { translate: false, value: contact.toAddress ? decodeEntities(contact.toAddress) : '' }
          case 'from_address':
            return { translate: false, value: contact.fromAddress ? decodeEntities(contact.fromAddress) : '' }
          case 'subject':
            return { translate: false, value: contact.subject }
          case 'silence_max_duration':
            if (contact.eventCalculations && contact.eventCalculations.silence) {
              return { translate: false, value: formatDuration(contact.eventCalculations.silence.maxDuration) }
            }
            break
          case 'silence_min_duration':
            if (contact.eventCalculations && contact.eventCalculations.silence) {
              return { translate: false, value: formatDuration(contact.eventCalculations.silence.minDuration) }
            }
            break
          case 'silence_average_duration':
            if (contact.eventCalculations && contact.eventCalculations.silence) {
              return { translate: false, value: formatDuration(contact.eventCalculations.silence.avgDuration) }
            }
            break
          case 'silence_total_duration':
            if (contact.eventCalculations && contact.eventCalculations.silence) {
              return { translate: false, value: formatDuration(contact.eventCalculations.silence.totalDuration) }
            } else {
              return { translate: false, value: formatDuration(0) }
            }
          case 'silence_total_events':
            if (contact.eventCalculations && contact.eventCalculations.silence) {
              return { translate: false, value: contact.eventCalculations.silence.totalEvents }
            }
            break
          case 'silence_percent_of_call':
            if (contact.eventCalculations && contact.eventCalculations.silence) {
              return { translate: false, value: `${contact.eventCalculations.silence.percentOfCall}%` }
            }
            break
          case 'talkover_max_duration':
            if (contact.eventCalculations && contact.eventCalculations.talkover) {
              return { translate: false, value: formatDuration(contact.eventCalculations.talkover.maxDuration) }
            }
            break
          case 'talkover_min_duration':
            if (contact.eventCalculations && contact.eventCalculations.talkover) {
              return { translate: false, value: formatDuration(contact.eventCalculations.talkover.minDuration) }
            }
            break
          case 'talkover_average_duration':
            if (contact.eventCalculations && contact.eventCalculations.talkover) {
              return { translate: false, value: formatDuration(contact.eventCalculations.talkover.avgDuration) }
            }
            break
          case 'talkover_total_duration':
            if (contact.eventCalculations && contact.eventCalculations.talkover) {
              return { translate: false, value: formatDuration(contact.eventCalculations.talkover.totalDuration) }
            } else {
              return { translate: false, value: formatDuration(0) }
            }
          case 'talkover_total_events':
            if (contact.eventCalculations && contact.eventCalculations.talkover) {
              return { translate: false, value: contact.eventCalculations.talkover.totalEvents }
            }
            break
          case 'talkover_percent_of_call':
            if (contact.eventCalculations && contact.eventCalculations.talkover) {
              return { translate: false, value: `${contact.eventCalculations.talkover.percentOfCall}%` }
            }
            break
          case 'pause_max_duration':
            if (contact.eventCalculations && contact.eventCalculations.pause) {
              return { translate: false, value: formatDuration(contact.eventCalculations.pause.maxDuration) }
            }
            break
          case 'pause_min_duration':
            if (contact.eventCalculations && contact.eventCalculations.pause) {
              return { translate: false, value: formatDuration(contact.eventCalculations.pause.minDuration) }
            }
            break
          case 'pause_average_duration':
            if (contact.eventCalculations && contact.eventCalculations.pause) {
              return { translate: false, value: formatDuration(contact.eventCalculations.pause.avgDuration) }
            }
            break
          case 'pause_total_duration':
            if (contact.eventCalculations && contact.eventCalculations.pause) {
              return { translate: false, value: formatDuration(contact.eventCalculations.pause.totalDuration) }
            } else {
              return { translate: false, value: formatDuration(0) }
            }
          case 'pause_total_events':
            if (contact.eventCalculations && contact.eventCalculations.pause) {
              return { translate: false, value: contact.eventCalculations.pause.totalEvents }
            }
            break
          case 'pause_percent_of_call':
            if (contact.eventCalculations && contact.eventCalculations.pause) {
              return { translate: false, value: `${contact.eventCalculations.pause.percentOfCall}%` }
            }
            break
          case 'hold_max_duration':
            if (contact.eventCalculations && contact.eventCalculations.hold) {
              return { translate: false, value: formatDuration(contact.eventCalculations.hold.maxDuration) }
            }
            break
          case 'hold_min_duration':
            if (contact.eventCalculations && contact.eventCalculations.hold) {
              return { translate: false, value: formatDuration(contact.eventCalculations.hold.minDuration) }
            }
            break
          case 'hold_average_duration':
            if (contact.eventCalculations && contact.eventCalculations.hold) {
              return { translate: false, value: formatDuration(contact.eventCalculations.hold.avgDuration) }
            }
            break
          case 'hold_total_duration':
            if (contact.eventCalculations && contact.eventCalculations.hold) {
              return { translate: false, value: formatDuration(contact.eventCalculations.hold.totalDuration) }
            } else {
              return { translate: false, value: formatDuration(0) }
            }
          case 'hold_total_events':
            if (contact.eventCalculations && contact.eventCalculations.hold) {
              return { translate: false, value: contact.eventCalculations.hold.totalEvents }
            }
            break
          case 'hold_percent_of_call':
            if (contact.eventCalculations && contact.eventCalculations.hold) {
              return { translate: false, value: `${contact.eventCalculations.hold.percentOfCall}%` }
            }
            break
          case 'end_of_call_workflow_rule':
            return { translate: false, value: decodeEntities(contact.endOfCallWorkflowRule) }
          case 'daily_workflow_rule':
            return { translate: false, value: contact.dailyWorkflowRule }
          case 'contact_type':
            return contact.contactType ? { translate: true, value: contact.contactType?.toLowerCase() } : { translate: false, value: '' }
          case 'recording_type':
            return { translate: true, value: contact.recordingType.toLowerCase() }
          case 'audioFileUploadStateId':
            return { translate: true, value: formatMediaDisplayState(contact.audioDisplayStateId) }
          case 'videoFileUploadStateId':
            return { translate: true, value: formatMediaDisplayState(contact.screenDisplayStateId) }
          case 'sentiment': {
            const sentimentState = sentiment.getStateFromScore(contact.sentiment)
            switch (sentimentState) {
              case sentiment.state.NEGATIVE:
                return { translate: true, value: 'negative' }
              case sentiment.state.POSITIVE:
                return { translate: true, value: 'positive' }
              case sentiment.state.UNSCORED:
                return { translate: false, value: '' }
              case sentiment.state.NEUTRAL:
              /* falls through */
              default:
                return { translate: true, value: 'neutral' }
            }
          }
          case 'assocCallId':
            return { translate: false, value: contact.assocCallId }
          case 'avayaDecimalUCID':
            return { translate: false, value: contact.avayaDecimalUCID }
          case 'external_party':
            return { translate: false, value: contact.externalParty }
          case 'text_preview':
            return contact.textSearchPreview ? { translate: true, value: contact.textSearchPreview } : { translate: false, value: 'noTextSearchPerformed' }
          case 'channel':
            return { translate: true, value: contact.channel }
          default:
            return { translate: false, value: '' }
        }
      })()
    }
    return {
      label: column.id,
      value,
    }
  })

  if (contact.id) {
    row.push({
      label: 'id',
      value: contact.id,
    })
  }

  // Add redacted column if it exists. This will not be displayed in the UI but will be used to redact data and not allow contacts to be viewed
  if (contact.redacted) {
    row.push({
      label: 'redacted',
      value: contact.redacted,
    })
  }

  return row.reduce((acc, curr) => {
    acc[curr.label] = curr.value
    return acc
  }, {})

  function formatYesNo(value) {
    return value ? 'yes' : 'no'
  }
}

export function formatDate(timestamp, timezone) {
  // I am doing this because I do not have a way to set moment.locale() globally and I cannot access useLocalizedMoment in this file
  const currentLocale = localStorage.getItem('selectedLanguageId') ?? 'en'
  moment.locale(currentLocale)
  const date = moment(timestamp)
  if (timezone) {
    date.tz(timezone)
  }
  return date.format('L')
}

export function formatDuration(duration) {
  const roundedDuration = Math.round(duration / 1000) * 1000
  return duration !== null && typeof duration !== 'undefined' ? toHumanReadable(roundedDuration) : ''
}

export function formatTime(timestamp, timezone) {
  return moment(timestamp)
    .tz(timezone)
    .format('hh:mm a')
    .toUpperCase()
}
